import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { CoffeeViewNavState } from '@/analytics/events';
import { CoffeePhoto } from '@/components/common/CoffeePhoto';
import { useImageFallback } from '@/hooks/useImageFallback';
import { useShelfToggle } from '@/hooks/useShelfToggle';
import { formatRelativeTime } from '@/lib/formatters';
import type { ShelfEntry } from '@/types/feed';

interface PastShelfCardProps {
  entry: ShelfEntry;
  // Re-syncs the shelf lists after a successful re-add.
  refetch: () => Promise<void>;
}

export function PastShelfCard({ entry, refetch }: PastShelfCardProps) {
  const { coffee, removedAt } = entry;
  const coffeeImageUrl = coffee.imageUrl ?? null;
  const { hasError, onError } = useImageFallback(coffeeImageUrl);
  const showImage = coffeeImageUrl !== null && !hasError;
  const roasterName = coffee.roaster?.name ?? 'Unknown roaster';
  // The Re-add button only renders while the coffee is off the shelf, so
  // the toggle always adds.
  const { isToggling, error, toggle } = useShelfToggle(coffee.isOnShelf, coffee.id, 'account');
  // isToggling drops as soon as the mutation resolves, BEFORE the refetch
  // settles — and until refetch lands the stale coffee.isOnShelf prop keeps
  // rendering an enabled Re-add button whose next click would dispatch a
  // REMOVE, reversing the user's action. Bracket the whole handler
  // (mutation + refetch), like the old page-level readdingId guard did.
  const [isPending, setIsPending] = useState(false);
  const isBusy = isToggling || isPending;

  const handleReadd = async () => {
    setIsPending(true);
    try {
      if (await toggle()) await refetch();
    } finally {
      setIsPending(false);
    }
  };

  return (
    <>
      <div
        className="bg-wolisho-card/50 border border-dashed border-wolisho-brown/10 rounded-xl p-4 flex items-center gap-4"
        data-testid="past-shelf-card"
      >
        <Link
          to={`/coffee/${coffee.id}`}
          state={{ source: 'shelf' } satisfies CoffeeViewNavState}
          className="flex items-center gap-4 flex-1 min-w-0"
        >
          {/* No image container when the coffee has no photo. */}
          {showImage && (
            <CoffeePhoto
              src={coffeeImageUrl}
              alt={coffee.name}
              sizeClassName="w-16 h-20"
              className="opacity-60"
              testId="past-shelf-card-photo"
              onError={onError}
            />
          )}
          {/* Divider between the framed tile and the copy, matching the coffee
              detail layout. Only with a photo — otherwise it's a stray edge. */}
          <div
            className={`flex-1 min-w-0 ${showImage ? 'border-l border-wolisho-brown/10 pl-4' : ''}`}
          >
            <p className="text-black/60 font-bold truncate">{coffee.name}</p>
            <p className="text-black/40 text-sm">
              by <span className="text-wolisho-red/50">{roasterName}</span>
            </p>
            <p className="text-black/40 text-sm">{coffee.origin}</p>
            {removedAt && (
              <p className="text-xs text-black/40 mt-1" data-testid="past-shelf-card-removed">
                Removed {formatRelativeTime(removedAt)}
              </p>
            )}
          </div>
        </Link>
        {coffee.isOnShelf ? (
          // Re-added coffees stay in history; re-adding again would be a no-op.
          <span
            className="shrink-0 text-[13px] font-bold text-black/35"
            data-testid="past-shelf-card-onshelf"
          >
            On shelf
          </span>
        ) : (
          <button
            type="button"
            data-testid="shelf-readd-button"
            disabled={isBusy}
            onClick={() => void handleReadd()}
            className="shrink-0 bg-transparent border-[1.5px] border-wolisho-red text-wolisho-red text-[13px] font-bold px-3.5 py-1.5 rounded-lg hover:bg-wolisho-red hover:text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={`Re-add ${coffee.name} to shelf`}
          >
            {isBusy ? 'Adding…' : 'Re-add'}
          </button>
        )}
      </div>
      {error && (
        <p
          role="alert"
          className="text-wolisho-red text-sm text-center"
          data-testid="past-shelf-card-error"
        >
          Failed to re-add coffee to shelf. Please try again.
        </p>
      )}
    </>
  );
}
